import type { ApiQueryParams } from '@frontend/api-utils';
import { Logger } from '@frontend/logger';
import { SettingKey } from '@frontend/setting/types';
import { TransactionUserType } from '@frontend/transaction/types';
import type { TransactionUser } from '@frontend/transaction/types';
import type { IUser, UserListResponse, UserQueryParams } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { OPEN_DROP_OFF_QR_USER_RESOLUTION_KEY } from '../../utils/open-drop-off-user-lookup';
import type { OpenDropOffQrUserResolution } from '../../utils/open-drop-off-user-lookup';
import { resolveUsersByGroupMemberOrder } from '../../utils/resolve-users-by-group-member-order';
import { syncTransactionUsers } from '../../utils/sync-transaction-users';
import { useScreen } from '../useScreen';

// eslint-disable-next-line no-unused-vars
type FetchUserOptions = (...args: [ApiQueryParams<UserQueryParams>?]) => Promise<UserListResponse>;
// eslint-disable-next-line no-unused-vars
type IndexedAction = (...args: [number]) => void;
// eslint-disable-next-line no-unused-vars
type UpdateSelectedUser = (...args: [number, string | null]) => void;

interface ViewProps {
    isLoading: boolean;
    selectedSenders: (string | null)[];
    selectedReceivers: (string | null)[];
    senderSubsets: (string[] | undefined)[];
    receiverSubsets: (string[] | undefined)[];
    canAddSender: boolean;
    canAddReceiver: boolean;
    fetchSenderOptions: FetchUserOptions;
    fetchReceiverOptions: FetchUserOptions;
    addSender(): void;
    addReceiver(): void;
    removeSender: IndexedAction;
    removeReceiver: IndexedAction;
    updateSender: UpdateSelectedUser;
    updateReceiver: UpdateSelectedUser;
    canContinue: boolean;
    onSubmit(): Promise<void>;
    onBack(): void;
}

const getSelectedUserIds = (transactionUsers: TransactionUser[] | undefined) =>
    (transactionUsers ?? []).map((transactionUser) => transactionUser.user_id).filter((userId): userId is string => !!userId);

const getInitialSelectedUsers = (storedUsers: TransactionUser[] | undefined, candidateUsers?: IUser[]) => {
    const storedUserIds = getSelectedUserIds(storedUsers);
    if (storedUserIds.length > 0) return storedUserIds;
    return candidateUsers?.length === 1 ? [candidateUsers[0].id] : [null];
};

const filterUsers = (users: IUser[], queryParams?: ApiQueryParams<UserQueryParams>): UserListResponse => {
    const search = typeof queryParams?.search === 'string' ? queryParams.search.trim().toLowerCase() : '';
    let filtered = users;

    if (search) {
        filtered = users.filter((user) => {
            const displayName = new User(user).getDisplayName().toLowerCase();
            const uniqueIdentifier = user.unique_identifier.toLowerCase();
            const email = user.email?.toLowerCase() ?? '';
            return displayName.includes(search) || uniqueIdentifier.includes(search) || email.includes(search);
        });
    }

    const index = Number.parseInt(String(queryParams?.index ?? '0'), 10);
    const size = Number.parseInt(String(queryParams?.size ?? '100'), 10);

    return {
        count: filtered.length,
        results: filtered.slice(index * size, (index + 1) * size)
    };
};

const getSelectionSubsets = (selectedUserIds: (string | null)[], users: IUser[] | null) =>
    selectedUserIds.map((selectedUserId) =>
        users
            ?.filter((user) => {
                const selectedElsewhere = selectedUserIds.find((userId) => userId === user.id);
                return selectedElsewhere ? selectedUserId === selectedElsewhere : true;
            })
            .map((user) => user.id)
    );

const useSelectSendersAndReceivers = (): ViewProps => {
    const { store, updateWizardData, onBack, onSubmit: onContinue } = useScreen();
    const { syncTransaction } = useWizardTransaction({
        wizardId: store?.wizard.id,
        store,
        autoCreate: false,
        autoFetch: true
    });
    const qrUserResolution = store?.data[OPEN_DROP_OFF_QR_USER_RESOLUTION_KEY] as OpenDropOffQrUserResolution | undefined;
    const [isLoading, setIsLoading] = useState(true);
    const [senders, setSenders] = useState<IUser[] | null>(qrUserResolution?.senderCandidates ?? null);
    const [receivers, setReceivers] = useState<IUser[] | null>(qrUserResolution?.receiverCandidates ?? null);
    const [selectedSenders, setSelectedSenders] = useState<(string | null)[]>(() =>
        getInitialSelectedUsers(store?.data.senders as TransactionUser[] | undefined, qrUserResolution?.senderCandidates)
    );
    const [selectedReceivers, setSelectedReceivers] = useState<(string | null)[]>(() =>
        getInitialSelectedUsers(store?.data.receivers as TransactionUser[] | undefined, qrUserResolution?.receiverCandidates)
    );
    const selectedSendersRef = useRef(selectedSenders);
    const selectedReceiversRef = useRef(selectedReceivers);

    useEffect(() => {
        selectedSendersRef.current = selectedSenders;
    }, [selectedSenders]);

    useEffect(() => {
        selectedReceiversRef.current = selectedReceivers;
    }, [selectedReceivers]);

    useEffect(() => {
        const senderUserGroupId = store?.functionalitySettings?.find((setting) => setting.key === SettingKey.OPEN_DROP_OFF_SENDERS_USER_GROUP_ID)?.value;
        const receiverUserGroupId = store?.functionalitySettings?.find((setting) => setting.key === SettingKey.OPEN_DROP_OFF_RECEIVERS_USER_GROUP_ID)?.value;

        const loadUsers = async () => {
            try {
                if (qrUserResolution) {
                    setSenders(qrUserResolution.senderCandidates);
                    setReceivers(qrUserResolution.receiverCandidates);
                    return;
                }

                const [orderedSenders, orderedReceivers] = await Promise.all([
                    senderUserGroupId && typeof senderUserGroupId === 'string' ? resolveUsersByGroupMemberOrder(senderUserGroupId) : Promise.resolve(null),
                    receiverUserGroupId && typeof receiverUserGroupId === 'string' ? resolveUsersByGroupMemberOrder(receiverUserGroupId) : Promise.resolve(null)
                ]);

                setSenders(orderedSenders);
                setReceivers(orderedReceivers);
            } catch (error) {
                Logger.error('Failed to load senders and receivers', error);
            } finally {
                setIsLoading(false);
            }
        };

        void loadUsers();
    }, [qrUserResolution, store?.functionalitySettings]);

    const senderSubsets = useMemo(() => getSelectionSubsets(selectedSenders, senders), [selectedSenders, senders]);
    const receiverSubsets = useMemo(() => getSelectionSubsets(selectedReceivers, receivers), [selectedReceivers, receivers]);
    const fetchSenderOptions = useCallback(
        (queryParams?: ApiQueryParams<UserQueryParams>) => Promise.resolve(filterUsers(senders ?? [], queryParams)),
        [senders]
    );
    const fetchReceiverOptions = useCallback(
        (queryParams?: ApiQueryParams<UserQueryParams>) => Promise.resolve(filterUsers(receivers ?? [], queryParams)),
        [receivers]
    );

    const updateSender = useCallback((index: number, senderId: string | null) => {
        const updated = [...selectedSendersRef.current];
        updated[index] = senderId;
        setSelectedSenders(updated);
    }, []);

    const updateReceiver = useCallback((index: number, receiverId: string | null) => {
        const updated = [...selectedReceiversRef.current];
        updated[index] = receiverId;
        setSelectedReceivers(updated);
    }, []);

    const addSender = () => setSelectedSenders([...selectedSendersRef.current, null]);
    const addReceiver = () => setSelectedReceivers([...selectedReceiversRef.current, null]);
    const removeSender = (index: number) => setSelectedSenders(selectedSendersRef.current.filter((_, i) => i !== index));
    const removeReceiver = (index: number) => setSelectedReceivers(selectedReceiversRef.current.filter((_, i) => i !== index));
    const validSenderIds = selectedSenders.filter((senderId): senderId is string => !!senderId);
    const validReceiverIds = selectedReceivers.filter((receiverId): receiverId is string => !!receiverId);
    const canContinue = validSenderIds.length > 0 && validReceiverIds.length > 0;

    const onSubmit = async () => {
        if (!store || !canContinue) return;

        setIsLoading(true);
        try {
            const accountId = store.wizard.account_id;
            const transaction = await syncTransaction();
            const [syncedSenders, syncedReceivers] = await Promise.all([
                syncTransactionUsers(
                    accountId,
                    transaction.id,
                    TransactionUserType.SENDER,
                    selectedSendersRef.current.filter((userId): userId is string => !!userId)
                ),
                syncTransactionUsers(
                    accountId,
                    transaction.id,
                    TransactionUserType.RECEIVER,
                    selectedReceiversRef.current.filter((userId): userId is string => !!userId)
                )
            ]);

            updateWizardData({
                senders: syncedSenders,
                receivers: syncedReceivers,
                transaction,
                [OPEN_DROP_OFF_QR_USER_RESOLUTION_KEY]: undefined
            });

            const nextStep = store.steps.find((step) => step.screen === ScreenType.SELECT_SENDERS_AND_RECEIVERS && step.trigger === ScreenTrigger.SUBMIT);
            onContinue(nextStep);
        } catch (error) {
            Logger.error('Failed to select open drop-off QR senders and receivers', error);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        selectedSenders,
        selectedReceivers,
        senderSubsets,
        receiverSubsets,
        canAddSender: selectedSenders.length < (senders?.length ?? 0),
        canAddReceiver: selectedReceivers.length < (receivers?.length ?? 0),
        fetchSenderOptions,
        fetchReceiverOptions,
        addSender,
        addReceiver,
        removeSender,
        removeReceiver,
        updateSender,
        updateReceiver,
        canContinue,
        onSubmit,
        onBack
    };
};

export default useSelectSendersAndReceivers;
